import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useFetchWithAuth } from '../../session/useFetchWithAuth'
import { adminDistrictUrl } from '../../utils/districtUrls'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface CreateDistrictInput {
  name: string
  city: string
}

interface UpdateDistrictInput {
  name: string
  city: string
}

export const useCreateDistrictMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateDistrictInput) => {
      const response = await fetchWithAuth(`${API_URL}/admin/districts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      return response
    },
    onError: (error: any) => {
      console.error('Create district error:', error)
      if (error.message?.includes('already exists') || error.status === 409) {
        toast.error(error.message || 'This district already exists')
      } else {
        toast.error(error.message || 'Failed to create district')
      }
    },
    onSuccess: () => {
      toast.success('District created successfully')
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
    },
  })
}

export const useUpdateDistrictMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      name,
      city,
      data,
    }: {
      name: string
      city: string
      data: UpdateDistrictInput
    }) => {
      const response = await fetchWithAuth(
        `${API_URL}${adminDistrictUrl({ name, city })}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      )
      return response
    },
    onError: (error: any) => {
      console.error('Update district error:', error)
      if (error.message?.includes('already exists') || error.status === 409) {
        toast.error(error.message || 'This district already exists')
      } else {
        toast.error(error.message || 'Failed to update district')
      }
    },
    onSuccess: () => {
      toast.success('District updated successfully')
      queryClient.invalidateQueries({ queryKey: ['adminDistrict'] })
      queryClient.invalidateQueries({ queryKey: ['districts'] })
      queryClient.invalidateQueries({ queryKey: ['public-districts'] })
      queryClient.invalidateQueries({ queryKey: ['schools'] })
      queryClient.invalidateQueries({ queryKey: ['public-schools'] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
    },
  })
}

export const useDeleteDistrictMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ name, city }: { name: string; city: string }) => {
      await fetchWithAuth(`${API_URL}${adminDistrictUrl({ name, city })}`, {
        method: 'DELETE',
      })
    },
    onError: (error: any) => {
      console.error('Delete district error:', error)
      toast.error(error.message || 'Failed to delete district')
    },
    onSuccess: () => {
      toast.success('District deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
    },
  })
}
